import React from 'react'
import { useMessaging } from './MessagingProvider'
import CoachMessageThread from './CoachMessageThread'
import NewMessage from './NewMessage'
import NewSupportMessage from './NewSupportMessage'
import ThreadList from './ThreadList'
import SentList, { SentMessage } from './SentList'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '~/lib/ui/resizable'
import { ScrollArea } from '~/lib/ui/scroll-area'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '~/lib/ui/tabs'
import useTranslation from '~/components/shared/TranslationProvider'
import { Button } from '~/lib/ui/button'
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectItem,
} from '~/lib/ui/select'
import { apiGet } from '~/util/api'
import { useQuery } from '@tanstack/react-query'

const Layout = ({ layoutHeight = 'calc(100vh - 310px)' }) => {
  const { isMobile } = useMessaging()

  return isMobile ? (
    <MobileLayout />
  ) : (
    <DesktopLayout layoutHeight={layoutHeight} />
  )
}
export default Layout

const InboxSentTabs = () => {
  const { t } = useTranslation('CoachMessageThread')
  const { viewingSentMessages, setViewingSentMessages, clearNewMessageMode } =
    useMessaging()
  const handleTabChange = (value) => {
    setViewingSentMessages(value === 'sent')
    clearNewMessageMode()
    window.location.hash = ''
  }

  return (
    <Tabs
      value={viewingSentMessages ? 'sent' : 'inbox'}
      onValueChange={handleTabChange}
    >
      <TabsList className="ml-4 w-3/4">
        <TabsTrigger className="w-1/2" value="inbox">
          {t('inbox')}
        </TabsTrigger>
        <TabsTrigger className="w-1/2" value="sent">
          {t('sent')}
        </TabsTrigger>
      </TabsList>
      <TabsContent value="inbox">
        <ThreadList />
      </TabsContent>
      <TabsContent value="sent">
        <SentList />
      </TabsContent>
    </Tabs>
  )
}

const SelectMessageType = () => {
  const { t } = useTranslation('CoachMessageThread')
  const {
    setNewMessageType,
    setNewMessageMode,
    coach,
    programIsSilver,
    userIsCoach,
    coach_candidates,
    recipientCandidate,
    setRecipientCandidate,
    candidatesCoachIsLiaison,
    resume_review_completed,
  } = useMessaging()
  const cancelNewMessage = () => {
    setNewMessageType(null)
    setNewMessageMode(false)
  }

  const queryFn = () => {
    return apiGet('/coach_messages/resume_review_completed.json')
  }

  const { error, data } = useQuery({
    queryKey: ['resumeReviewStatus'],
    queryFn,
    enabled: !!coach,
  })

  if (error) {
    console.error('Error fetching resume review status:', error)
  }

  const resumeReviewClosed =
    data?.data?.resume_review_closed || resume_review_completed

  return (
    <div className="flex flex-col gap-y-2 p-4">
      {!userIsCoach && (
        <>
          {coach &&
            (!programIsSilver || (programIsSilver && !resumeReviewClosed)) &&
            !candidatesCoachIsLiaison && (
              <Button
                variant="coachcta"
                onClick={() => setNewMessageType('coach')}
              >
                {t('message_your_coach')}
              </Button>
            )}
          <Button
            variant="coachcta"
            onClick={() => setNewMessageType('tech_support')}
          >
            {t('message_technical_support')}
          </Button>
          <Button
            variant="coachcta"
            onClick={() => setNewMessageType('feedback')}
          >
            {t('send_feedback')}
          </Button>
          {coach && (
            <Button
              variant="coachcta"
              onClick={() => setNewMessageType('coach_support')}
            >
              {t('message_coach_support')}
            </Button>
          )}
        </>
      )}
      {userIsCoach && (
        <>
          <Select onValueChange={(value) => setRecipientCandidate(value)}>
            <SelectTrigger>
              <SelectValue placeholder={'Select a Candidate'} />
            </SelectTrigger>
            <SelectContent>
              {coach_candidates.map((candidate) => (
                <SelectItem
                  key={'coach_message' + candidate.id}
                  value={candidate}
                >
                  {candidate.first_name} {candidate.last_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            disabled={!recipientCandidate}
            onClick={() => setNewMessageType('coach')}
          >
            Message this Candidate
          </Button>
        </>
      )}
      <Button variant="outline" onClick={cancelNewMessage}>
        {t('cancel')}
      </Button>
    </div>
  )
}

const MobileLayout = () => {
  const {
    newMessageMode,
    newMessageType,
    currentThreadId,
    viewingSentMessages,
  } = useMessaging()

  const currentMobileView = () => {
    if (newMessageMode) {
      if (newMessageType) {
        const NewMessageComp =
          newMessageType === 'coach' ? NewMessage : NewSupportMessage
        return (
          <div className="p-4">
            <NewMessageComp />
          </div>
        )
      } else {
        return <SelectMessageType />
      }
    } else if (viewingSentMessages) {
      return (
        <div className="p-4">
          <SentMessage />
        </div>
      )
    } else if (currentThreadId) {
      return (
        <div className="p-4">
          <CoachMessageThread />
        </div>
      )
    } else {
      return <InboxSentTabs />
    }
  }
  return <div>{currentMobileView()}</div>
}

const DesktopLayout = ({ defaultLayout = [30, 70], layoutHeight }) => {
  const { newMessageType, newMessageMode, viewingSentMessages } = useMessaging()
  const NewMessageComp = newMessageType
    ? newMessageType == 'coach'
      ? NewMessage
      : NewSupportMessage
    : SelectMessageType
  const ViewMessageComp = viewingSentMessages ? SentMessage : CoachMessageThread

  return (
    <ResizablePanelGroup direction="horizontal" className="h-full md:px-5">
      <ResizablePanel defaultSize={defaultLayout[0]} minSize={20}>
        <div className="pr-4 pt-4">
          <ScrollArea style={{ height: layoutHeight }}>
            <InboxSentTabs />
          </ScrollArea>
        </div>
      </ResizablePanel>
      <ResizableHandle withHandle className="bg-white" />
      <ResizablePanel
        defaultSize={defaultLayout[1]}
        minSize={30}
        style={{ height: layoutHeight }}
        className="flex flex-col overflow-y-auto p-4"
      >
        {newMessageMode ? (
          <NewMessageComp />
        ) : (
          <ViewMessageComp layoutHeight={layoutHeight} />
        )}
      </ResizablePanel>
    </ResizablePanelGroup>
  )
}
